/**
 * CreateModuleModal — Modal para crear un nuevo trabajo del Workspace.
 *
 * Extraído de InternalWorkspace.jsx en el refactor 2026-02-08 (cero
 * cambios funcionales: mismas props, mismos data-testids, misma
 * validación de base_type canónico).
 *
 * Exporta también `CANONICAL_BASE_TYPES` para que el ChangeTypeModal
 * (y cualquier otro consumidor) pueda re-usar la lista cerrada.
 */
import React, { useState } from 'react';
import { Plus, X, Loader2, Layout, Table2, Box } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';

// Lista cerrada de tipos canonicos (regla 1 del Workspace).
export const CANONICAL_BASE_TYPES = ['Dashboard', 'Tienda', 'Landing', 'Sitio Web', 'Panel'];

export const CreateModuleModal = ({ isOpen, onClose, onCreate }) => {
  const { language } = useLanguage();
  const [name, setName] = useState('');
  const [type, setType] = useState('dashboard');
  // Tipo BASE canonico (regla 4 — dropdown obligatorio).
  const [baseType, setBaseType] = useState('');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const t = language === 'es' ? {
    title: 'Crear Nuevo Trabajo',
    name: 'Nombre',
    namePlaceholder: 'Ej: Dashboard de Ventas',
    type: 'Tipo',
    baseType: 'Tipo Base',
    baseTypePlaceholder: 'Selecciona un tipo...',
    baseTypeHint: 'El tipo base se fija al cambiarlo. Solo Premium puede modificar.',
    description: 'Descripción (opcional)',
    descriptionPlaceholder: 'Describe brevemente este trabajo...',
    cancel: 'Cancelar',
    create: 'Crear Trabajo',
    creating: 'Creando...',
    types: {
      dashboard: 'Dashboard',
      table: 'Tabla',
      custom: 'Personalizado'
    }
  } : {
    title: 'Create New Work',
    name: 'Name',
    namePlaceholder: 'E.g.: Sales Dashboard',
    type: 'Type',
    baseType: 'Base Type',
    baseTypePlaceholder: 'Select a type...',
    baseTypeHint: 'The base type locks after first change. Only Premium can modify.',
    description: 'Description (optional)',
    descriptionPlaceholder: 'Briefly describe this work...',
    cancel: 'Cancel',
    create: 'Create Work',
    creating: 'Creating...',
    types: {
      dashboard: 'Dashboard',
      table: 'Table',
      custom: 'Custom'
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(language === 'es' ? 'El nombre es obligatorio' : 'Name is required');
      return;
    }
    if (!baseType || !CANONICAL_BASE_TYPES.includes(baseType)) {
      toast.error(language === 'es' ? 'Selecciona un tipo base' : 'Select a base type');
      return;
    }

    setIsCreating(true);
    try {
      await onCreate({ name: name.trim(), type, base_type: baseType, description: description.trim() });
      setName('');
      setType('dashboard');
      setBaseType('');
      setDescription('');
      onClose();
    } catch (error) {
      console.error('Error creating module:', error);
      // Si el backend bloqueo por cuota Free, cerramos este modal: la friccion
      // ya se comunica via toast + WorkspaceUpsellModal, no tiene sentido
      // mantener este formulario abierto.
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 403 && detail?.error === 'free_quota_exceeded') {
        setName('');
        setType('dashboard');
        setBaseType('');
        setDescription('');
        onClose();
      }
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">{t.title}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Nombre */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {t.name} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.namePlaceholder}
              className="w-full bg-slate-900 text-white border border-slate-600 rounded-lg px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              autoFocus
            />
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {t.type}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {['dashboard', 'table', 'custom'].map((typeOption) => (
                <button
                  key={typeOption}
                  type="button"
                  onClick={() => setType(typeOption)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-all ${
                    type === typeOption
                      ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                      : 'border-slate-600 bg-slate-900 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  {typeOption === 'dashboard' && <Layout className="w-5 h-5" />}
                  {typeOption === 'table' && <Table2 className="w-5 h-5" />}
                  {typeOption === 'custom' && <Box className="w-5 h-5" />}
                  <span className="text-xs">{t.types[typeOption]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tipo BASE canonico (regla 4 — dropdown OBLIGATORIO). */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {t.baseType} <span className="text-red-400">*</span>
            </label>
            <select
              value={baseType}
              onChange={(e) => setBaseType(e.target.value)}
              required
              data-testid="create-module-base-type-select"
              className="w-full bg-slate-900 text-white border border-slate-600 rounded-lg px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            >
              <option value="">{t.baseTypePlaceholder}</option>
              {CANONICAL_BASE_TYPES.map((bt) => (
                <option key={bt} value={bt}>{bt}</option>
              ))}
            </select>
            <p className="text-[11px] text-slate-500 mt-1">{t.baseTypeHint}</p>
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {t.description}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t.descriptionPlaceholder}
              rows={3}
              className="w-full bg-slate-900 text-white border border-slate-600 rounded-lg px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none"
            />
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={isCreating || !name.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t.creating}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  {t.create}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


export default CreateModuleModal;
